import { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import {
  COMPANY_FILTER_ALL,
  DOCUMENT_FILTERS,
  EMPTY_DOCUMENTS_MESSAGE,
  buildQuoteAcceptConfirmationMessage,
  buildQuoteRefuseConfirmationMessage,
  documentOrganizationLabel,
  filterDocuments,
  filterDocumentsByCompany,
  formatDocumentAmount,
  formatDocumentDate,
  listLinkedOrganizations,
  merchantDocumentStatusBadge,
  publishMerchantDocuments,
  shouldShowCompanyFilter,
  sortDocuments,
} from './documents/merchantDocumentsModel';

const ACTION_LABELS = {
  VIEW: 'Consulter',
  DOWNLOAD: 'Télécharger',
  ACCEPT: 'Accepter',
  REFUSE: 'Refuser',
};

function StatusCell({ doc }) {
  const statusBadge = merchantDocumentStatusBadge(doc);
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={`inline-flex max-w-full rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge.className}`}
      >
        {statusBadge.label}
      </span>
      {doc.actionRequired ? (
        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
          Action requise
        </span>
      ) : null}
    </div>
  );
}

function DocumentActions({ doc, busy, onView, onDownload, onAccept, onRefuse }) {
  const actions = Array.isArray(doc.availableActions) ? doc.availableActions : [];
  return (
    <div className="flex flex-wrap gap-2">
      {actions.includes('VIEW') ? (
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
          onClick={() => onView(doc)}
          disabled={busy}
        >
          {doc.actionRequired ? ACTION_LABELS.VIEW : 'Voir'}
        </button>
      ) : null}
      {actions.includes('DOWNLOAD') ? (
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
          onClick={() => onDownload(doc)}
          disabled={busy}
        >
          {ACTION_LABELS.DOWNLOAD}
        </button>
      ) : null}
      {actions.includes('ACCEPT') ? (
        <button
          type="button"
          className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
          onClick={() => onAccept(doc)}
          disabled={busy}
        >
          {ACTION_LABELS.ACCEPT}
        </button>
      ) : null}
      {actions.includes('REFUSE') ? (
        <button
          type="button"
          className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-60"
          onClick={() => onRefuse(doc)}
          disabled={busy}
        >
          {ACTION_LABELS.REFUSE}
        </button>
      ) : null}
    </div>
  );
}

export default function MerchantDocuments() {
  const [documents, setDocuments] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [companyFilter, setCompanyFilter] = useState(COMPANY_FILTER_ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [refuseTarget, setRefuseTarget] = useState(null);
  const [refuseNote, setRefuseNote] = useState('');
  const [acceptTarget, setAcceptTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.listMerchantDocuments();
      const next = sortDocuments(data?.documents || []);
      setDocuments(next);
      publishMerchantDocuments(next);
    } catch (err) {
      setError(err?.message || 'Impossible de charger les documents.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const linkedOrganizations = useMemo(
    () => listLinkedOrganizations(documents),
    [documents],
  );
  const showCompanyFilter = shouldShowCompanyFilter(documents);

  const visible = useMemo(() => {
    const byType = filterDocuments(documents, filter);
    return filterDocumentsByCompany(byType, companyFilter);
  }, [documents, filter, companyFilter]);

  async function runPdf(doc, mode) {
    setBusyId(doc.id);
    setError('');
    try {
      await apiService.openMerchantDocumentPdf(doc.type, doc.id, {
        mode,
        number: doc.number,
      });
    } catch (err) {
      setError(err?.message || 'PDF indisponible.');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmAccept() {
    if (!acceptTarget) return;
    setBusyId(acceptTarget.id);
    setError('');
    try {
      await apiService.acceptMerchantQuote(acceptTarget.id);
      setAcceptTarget(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Acceptation impossible.');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmRefuse() {
    if (!refuseTarget) return;
    setBusyId(refuseTarget.id);
    setError('');
    try {
      await apiService.refuseMerchantQuote(refuseTarget.id, refuseNote.trim());
      setRefuseTarget(null);
      setRefuseNote('');
      await load();
    } catch (err) {
      setError(err?.message || 'Refus impossible.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Mes documents</h1>
        <p className="mt-1 text-sm text-gray-600">
          Retrouvez vos devis, contrats, factures et avoirs Clickorico.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Filtrer les documents">
        {DOCUMENT_FILTERS.map((item) => {
          const active = filter === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`rounded-full px-3 py-1.5 text-sm ${
                active
                  ? 'bg-purple-800 text-white'
                  : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {showCompanyFilter ? (
        <div
          className="mb-4 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Filtrer par société"
        >
          <button
            type="button"
            role="tab"
            aria-selected={companyFilter === COMPANY_FILTER_ALL}
            className={`rounded-full px-3 py-1.5 text-sm ${
              companyFilter === COMPANY_FILTER_ALL
                ? 'bg-slate-800 text-white'
                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => setCompanyFilter(COMPANY_FILTER_ALL)}
          >
            Toutes les sociétés
          </button>
          {linkedOrganizations.map((org) => {
            const active = companyFilter === org.id;
            return (
              <button
                key={org.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`rounded-full px-3 py-1.5 text-sm ${
                  active
                    ? 'bg-slate-800 text-white'
                    : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setCompanyFilter(org.id)}
              >
                {org.displayName}
              </button>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Chargement des documents…</p>
      ) : null}

      {!loading && !error && visible.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-sm text-gray-600">
          {documents.length === 0
            ? EMPTY_DOCUMENTS_MESSAGE
            : 'Aucun document dans cette catégorie.'}
        </p>
      ) : null}

      {!loading && visible.length > 0 ? (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white md:block">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Société</th>
                  <th className="px-4 py-3">Numéro</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Montant</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((doc) => (
                  <tr key={`${doc.type}-${doc.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{doc.typeLabel}</td>
                    <td className="px-4 py-3 text-gray-800">{documentOrganizationLabel(doc)}</td>
                    <td className="px-4 py-3 text-gray-800">{doc.number}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDocumentDate(doc.date)}</td>
                    <td className="px-4 py-3 text-gray-800">
                      {formatDocumentAmount(doc.amountTtcCents, doc.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusCell doc={doc} />
                    </td>
                    <td className="px-4 py-3">
                      <DocumentActions
                        doc={doc}
                        busy={busyId === doc.id}
                        onView={(row) => runPdf(row, 'preview')}
                        onDownload={(row) => runPdf(row, 'download')}
                        onAccept={setAcceptTarget}
                        onRefuse={(row) => {
                          setRefuseNote('');
                          setRefuseTarget(row);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="space-y-3 md:hidden">
            {visible.map((doc) => (
              <li key={`${doc.type}-${doc.id}`} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{doc.typeLabel}</p>
                    <p className="text-sm text-gray-600">{documentOrganizationLabel(doc)}</p>
                    <p className="text-sm text-gray-700">{doc.number}</p>
                  </div>
                  <StatusCell doc={doc} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <dt className="text-gray-500">Date</dt>
                    <dd className="text-gray-900">{formatDocumentDate(doc.date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Montant</dt>
                    <dd className="text-gray-900">
                      {formatDocumentAmount(doc.amountTtcCents, doc.currency)}
                    </dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <DocumentActions
                    doc={doc}
                    busy={busyId === doc.id}
                    onView={(row) => runPdf(row, 'preview')}
                    onDownload={(row) => runPdf(row, 'download')}
                    onAccept={setAcceptTarget}
                    onRefuse={(row) => {
                      setRefuseNote('');
                      setRefuseTarget(row);
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {acceptTarget ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold text-gray-900">Accepter le devis</h2>
            <p className="mt-2 text-sm text-gray-600">
              {buildQuoteAcceptConfirmationMessage(acceptTarget)}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md px-3 py-2 text-sm text-gray-700"
                onClick={() => setAcceptTarget(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white"
                onClick={confirmAccept}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {refuseTarget ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" role="dialog" aria-modal="true">
            <h2 className="text-lg font-semibold text-gray-900">Refuser le devis</h2>
            <p className="mt-2 text-sm text-gray-600">
              {buildQuoteRefuseConfirmationMessage(refuseTarget)} Le motif est facultatif.
            </p>
            <label className="mt-3 block text-sm text-gray-700" htmlFor="refuse-note">
              Motif
              <textarea
                id="refuse-note"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={3}
                value={refuseNote}
                onChange={(event) => setRefuseNote(event.target.value)}
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md px-3 py-2 text-sm text-gray-700"
                onClick={() => setRefuseTarget(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white"
                onClick={confirmRefuse}
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
